import React, { useCallback, useEffect, useState } from 'react';
import { getTheme } from '../theme';
import { API, authHeaders, startLightspeedConnect, startStripeConnect } from '../utils/helpers';

// The owner's setup guide (18 Oct 2026). Manny: "Most shop owners are not
// super technical and we should walk them through it ... otherwise I can
// expect calls." Every step says what it is, why it matters, opens exactly the
// right place, and has a step-by-step "Show me how". States come from the
// server's live checks (routes/gettingStarted.js), so steps tick themselves.

const ORANGE = '#F4622A';
const GREEN = '#2ecc71';

export default function GettingStarted({ theme = 'dark', onOpenSettings, onStatus, onFinish, returnNotice }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/getting-started`, { credentials: 'include', headers: authHeaders() });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.error || 'Could not load your setup steps.');
      setGuide(out); setError('');
      if (onStatus) onStatus(out);
    } catch (err) { setError(err.message); }
  }, [onStatus]);

  useEffect(() => { load(); }, [load]);
  // Coming back from Stripe, Lightspeed or the booking page in another tab:
  // check again, so the step they just finished ticks without a reload.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const act = async (step) => {
    const a = step.action;
    if (!a) return;
    setBusy(step.key);
    try {
      if (a.type === 'lightspeed') await startLightspeedConnect();
      else if (a.type === 'stripe') await startStripeConnect();
      else if (a.type === 'settings') onOpenSettings(a.group);
      else if (a.type === 'link') window.open(a.url, step.key === 'subscription' ? '_self' : '_blank', 'noopener');
    } catch (err) { setError(err.message); }
    setBusy('');
  };

  if (error && !guide) return <div style={{ color: T.textSub, fontSize: 14 }}>{error}</div>;
  if (!guide) return <div style={{ color: T.textSub, fontSize: 14 }}>Loading your setup steps…</div>;

  const next = guide.steps.find(s => !s.optional && !s.info && s.state !== 'done' && !s.blockedBy);
  const pct = Math.round((guide.doneCount / guide.requiredCount) * 100);
  const chip = (s) => {
    if (s.state === 'done') return ['Done', GREEN];
    if (s.state === 'waiting') return ['We are on it', '#3498db'];
    if (s.state === 'later') return ['When you are ready', T.textSub];
    if (s.optional) return ['Optional', T.textSub];
    return ['To do', ORANGE];
  };
  const card = { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12 };
  const btn = (primary) => ({
    border: primary ? 'none' : `1px solid ${T.border}`, background: primary ? ORANGE : 'transparent', color: primary ? '#fff' : T.text,
    borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  });

  return (
    <div style={{ maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: 26, fontWeight: 700, color: T.text }}>
          {guide.complete ? `${guide.shopName} is ready` : `Welcome to Spinboard. Let's get ${guide.shopName} ready.`}
        </div>
        <div style={{ fontSize: 14, color: T.textSub, marginTop: 6, lineHeight: 1.5 }}>
          {guide.complete
            ? 'Everything Spinboard needs is connected. We will go through the rest together in your onboarding meeting.'
            : 'These are the connections Spinboard needs to work. Each has a button that takes you to the right place, and "Show me how" walks you through it. Steps tick themselves once they are done. Everything else we go through together in your onboarding meeting.'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14 }}>
          <div style={{ flex: 1, height: 8, borderRadius: 4, background: isDark ? '#243050' : '#e0e0d8', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: GREEN, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 13, color: T.textSub, whiteSpace: 'nowrap' }}>{guide.doneCount} of {guide.requiredCount} done</div>
        </div>
      </div>

      {returnNotice && (
        <div role="status" style={{ ...card, padding: '12px 16px', borderColor: returnNotice.ok ? GREEN : '#e74c3c', color: T.text, fontSize: 14 }}>
          {returnNotice.text}
        </div>
      )}
      {error && <div style={{ color: '#e74c3c', fontSize: 13 }}>{error}</div>}

      {guide.steps.map((s, i) => {
        const [label, colour] = chip(s);
        const isNext = next && next.key === s.key;
        const expanded = open[s.key] !== undefined ? open[s.key] : isNext;
        const done = s.state === 'done';
        return (
          <div key={s.key} style={{ ...card, padding: 18, borderColor: isNext ? ORANGE : T.cardBorder, borderWidth: isNext ? 2 : 1 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <span aria-hidden="true" style={{ width: 28, height: 28, flex: '0 0 28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 700, background: done ? GREEN : isNext ? ORANGE : (isDark ? '#243050' : '#e0e0d8'), color: done || isNext ? '#fff' : T.textSub }}>
                {done ? '✓' : s.info ? 'i' : i + 1}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{s.title}</div>
                  <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 10, border: `1px solid ${colour}`, color: colour }}>{label}</span>
                  {isNext && <span style={{ fontSize: 11, fontWeight: 700, color: ORANGE }}>Next up</span>}
                </div>
                <div style={{ fontSize: 14, color: T.textSub, marginTop: 6, lineHeight: 1.5 }}>{done && s.done ? s.done : s.why}</div>

                {!done && !s.info && (
                  <>
                    {s.blockedBy && <div style={{ fontSize: 13, color: ORANGE, marginTop: 8 }}>{s.blockedBy}</div>}
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
                      {s.action && (
                        <button style={{ ...btn(true), opacity: s.blockedBy || busy === s.key ? 0.5 : 1 }} disabled={!!s.blockedBy || busy === s.key} onClick={() => act(s)}>
                          {busy === s.key ? 'Opening…' : s.action.label}
                        </button>
                      )}
                      {s.how && (
                        <button style={btn(false)} onClick={() => setOpen(o => ({ ...o, [s.key]: !expanded }))} aria-expanded={expanded}>
                          {expanded ? 'Hide the steps' : 'Show me how'}
                        </button>
                      )}
                      {s.link && <a href={s.link.url} target="_blank" rel="noreferrer" style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>{s.link.label}</a>}
                    </div>
                    {expanded && (
                      <div style={{ marginTop: 14, padding: '14px 16px', borderRadius: 10, background: isDark ? '#141c2c' : '#f6f6f1', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {s.prepare && (
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6 }}>Have these ready</div>
                            <ul style={{ margin: 0, paddingLeft: 20, color: T.text, fontSize: 14, lineHeight: 1.55 }}>
                              {s.prepare.map(p => <li key={p}>{p}</li>)}
                            </ul>
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 6 }}>Step by step</div>
                          <ol style={{ margin: 0, paddingLeft: 22, color: T.text, fontSize: 14, lineHeight: 1.6 }}>
                            {s.how.map(h => <li key={h}>{h}</li>)}
                          </ol>
                        </div>
                        {s.help && <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5 }}>{s.help}</div>}
                      </div>
                    )}
                  </>
                )}
                {done && s.link && (
                  <a href={s.link.url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, color: T.text, fontSize: 13, fontWeight: 600 }}>{s.link.label}</a>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <div style={{ ...card, padding: '14px 18px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.5 }}>
          Stuck on anything? Reply to the welcome email we sent you and we will walk you through it.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={btn(false)} onClick={load}>Check again</button>
          {onFinish && <button style={btn(guide.complete)} onClick={onFinish}>{guide.complete ? 'Go to the service queue' : 'I will finish later'}</button>}
        </div>
      </div>
    </div>
  );
}
